import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from './AuthContext';

interface WishlistContextType {
  wishlistIds: string[];
  toggleWishlist: (productId: string) => Promise<void>;
  isInWishlist: (productId: string) => boolean;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [localWishlistIds, setLocalWishlistIds] = useState<string[]>([]);
  const [firestoreWishlistIds, setFirestoreWishlistIds] = useState<string[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('anon_wishlist');
    if (saved) {
      try {
        setLocalWishlistIds(JSON.parse(saved));
      } catch (e) {
        console.error('Error parsing wishlist from localStorage', e);
      }
    }
  }, []);

  // Persist to localStorage whenever localWishlistIds change
  useEffect(() => {
    localStorage.setItem('anon_wishlist', JSON.stringify(localWishlistIds));
  }, [localWishlistIds]);

  useEffect(() => {
    if (!user) {
      setFirestoreWishlistIds([]);
      return;
    }

    const unsub = onSnapshot(doc(db, 'wishlists', user.uid), (doc) => {
      if (doc.exists()) {
        const ids = doc.data().productIds || [];
        setFirestoreWishlistIds(ids);
        // Also sync local if needed? The user prompt implies logged in users should have them retained.
        // If we want them to stay synced, we should merge the firestore ones into local as well when logging in?
      } else {
        setFirestoreWishlistIds([]);
      }
    });

    return () => unsub();
  }, [user]);

  // Merge ids for display
  const wishlistIds = Array.from(new Set([...localWishlistIds, ...firestoreWishlistIds]));

  const toggleWishlist = async (productId: string) => {
    const isLocal = localWishlistIds.includes(productId);
    const newLocalIds = isLocal
      ? localWishlistIds.filter((id) => id !== productId)
      : [...localWishlistIds, productId];
    
    setLocalWishlistIds(newLocalIds);

    if (user) {
      const isFirestore = firestoreWishlistIds.includes(productId);
      const newFirestoreIds = isFirestore
        ? firestoreWishlistIds.filter((id) => id !== productId)
        : [...firestoreWishlistIds, productId];

      try {
        await setDoc(doc(db, 'wishlists', user.uid), {
          userId: user.uid,
          productIds: newFirestoreIds,
          updatedAt: new Date().toISOString(),
        });
      } catch (error) {
        console.error('Error updating wishlist:', error);
      }
    }
  };

  const isInWishlist = (productId: string) => wishlistIds.includes(productId);

  return (
    <WishlistContext.Provider value={{ wishlistIds, toggleWishlist, isInWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
}
